import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useOrderableModuleFeatures } from '../../views/settings/billing/functions/useOrderableModuleFeatures';
import ModuleOrderModal from '../../views/settings/billing/ModuleOrderModal';

interface ModuleOrderLinkProps {
    feature?: string;
    onOrdered?: () => void;
};

// Text link which orders a module via the order modal or links to the upgrade wizard
const ModuleOrderLink: React.FC<ModuleOrderLinkProps> = ({ feature, onOrdered }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { environmentType } = useAuthContext();
    const moduleFeatures = useOrderableModuleFeatures();

    // Determine if the feature is orderable as module within the current package
    const isOrderable = !!feature && !!moduleFeatures?.orderableFeatures.includes(feature);

    // Open the module order modal with the module information and payment button
    const handleOrderModuleClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        initializeModal(
            <ModuleOrderModal feature={feature!} onSuccess={onOrdered} />, {
                modalSize: 'small-medium'
            }
        );
    };

    // For trials the module is ordered within the wizard instead of a standalone payment
    if (isOrderable && environmentType === 'trial') {
        return (
            <Link className='module-order-link'
                  to={`/settings/billing/subscription/wizard?step=additional-modules&module=${feature}`}>
                {t('billing.order_module_link_label')}
                <FontAwesomeIcon icon={faArrowRight} />
            </Link>
        );
    }

    // Show the order link which opens the module order modal
    if (isOrderable) {
        return (
            <span className='module-order-link' onClick={handleOrderModuleClick}>
                {t('billing.order_module_link_label')}
                <FontAwesomeIcon icon={faArrowRight} />
            </span>
        );
    }

    // Otherwise show the upgrade link which preselects the package containing the feature
    return (
        <Link className='module-order-link'
              to={`/settings/billing/subscription/wizard?step=package${feature ? `&feature=${feature}` : ''}`}>
            {t('billing.upgrade_package_link_label')}
            <FontAwesomeIcon icon={faArrowRight} />
        </Link>
    );
};

export default ModuleOrderLink;
